'use client';

/**
 * Pure renderer for BlockSlide: SlideFrame + optional scrim + absolutely
 * positioned blocks. Shared by PDF export, deck view, and the editor canvas.
 */

import QRCode from 'react-qr-code';
import type { Anchor, Block, Scrim } from '@/lib/deck/blocks';
import { ANCHORS, ANCHOR_POSITIONS, anchorParts } from '@/lib/deck/blocks';
import type { BlockSlide } from '@/lib/deck/types';
import { EditableText } from './EditableText';
import { SlideFrame } from './SlideFrame';
import { ACCENT, BODY_DECK, CAPTION, DECK_BULLET_GLYPH, DECK_BULLET_LIST, DECK_BULLET_MARKER, DECK_BULLET_ROW, DECK_BULLET_TEXT, EYEBROW, H1, LEDE_DECK, QUOTE_SM } from './tokens';
import type { TextBlock } from '@/lib/deck/blocks';

/**
 * Per-kind spacing applied below each block within an anchor cell. Mirrors the
 * Prairies / CNE pitch deck cadence:
 *   eyebrow → mb-6 (24)
 *   headline → mb-8 (32)
 *   subheadline → mb-10 (40)
 *   lede → mb-6 (24)
 *   body → mb-4 (16)
 *   caption → mb-3 (12)
 *   accent → mb-4 (16)
 *   logo → mb-8 (32)   (for stacked layouts where the logo leads other blocks)
 *   bullets / conversation / qr → mb-6 (24)
 * Last block in the cell drops its bottom margin to 0.
 */
export const SPACING_AFTER_KIND: Record<string, number> = {
  eyebrow: 24,
  headline: 32,
  subheadline: 40,
  lede: 24,
  body: 16,
  caption: 12,
  accent: 16,
  logo: 32,
  bullets: 32,
  conversation: 24,
  qr: 24,
};

/** Slide is fixed 1920px wide; each 1% in block.w → this many pixels. */
export const SLIDE_PX_PER_PCT = 19.2;

export function spacingAfterKind(kind: string): number {
  return SPACING_AFTER_KIND[kind] ?? 24;
}

export function scrimToCss(s: Scrim): string {
  const stops = [...s.stops].sort((a, b) => a.at - b.at);
  const parts = stops.map((st) => {
    const col = s.color?.startsWith('#')
      ? `color-mix(in srgb, ${s.color} ${Math.round(st.opacity * 100)}%, transparent)`
      : `rgba(0,0,0,${st.opacity})`;
    return `${col} ${st.at}%`;
  });

  const dir: Record<Scrim['direction'], string> = {
    top: 'to bottom',
    bottom: 'to top',
    left: 'to right',
    right: 'to left',
    'top-right': 'to bottom left',
    'bottom-right': 'to top left',
    'top-left': 'to bottom right',
    'bottom-left': 'to top right',
  };

  const line = dir[s.direction] ?? 'to bottom';
  if (parts.length === 0) return 'transparent';
  return `linear-gradient(${line}, ${parts.join(', ')})`;
}

export function anchorPositionStyle(anchor: Anchor, x: number, y: number): React.CSSProperties {
  const { v, h } = anchorParts(anchor);

  const style: React.CSSProperties = {};
  if (h === 'left') style.left = `${x}%`;
  else if (h === 'right') style.right = `${100 - x}%`;
  else style.left = `${x}%`;

  if (v === 'top') style.top = `${y}%`;
  else if (v === 'bottom') style.bottom = `${100 - y}%`;
  else style.top = `${y}%`;

  const transforms: string[] = [];
  if (h === 'center') transforms.push('translateX(-50%)');
  if (v === 'center') transforms.push('translateY(-50%)');
  if (transforms.length) style.transform = transforms.join(' ');

  return style;
}

/**
 * Style for an anchor "cell" — the wrapper that pins itself to one of the 9
 * canonical anchor points and lays out its child blocks as a flex column with
 * consistent vertical spacing. Replaces freeform per-block positioning so
 * stacked blocks share an identical gap.
 */
export function anchorCellStyle(anchor: Anchor): React.CSSProperties {
  const { v, h } = anchorParts(anchor);
  const { x, y } = ANCHOR_POSITIONS[anchor];

  const style: React.CSSProperties = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
  };

  if (h === 'left') {
    style.left = `${x}%`;
    style.alignItems = 'flex-start';
  } else if (h === 'right') {
    style.right = `${100 - x}%`;
    style.alignItems = 'flex-end';
  } else {
    style.left = `${x}%`;
    style.alignItems = 'center';
  }

  if (v === 'top') {
    style.top = `${y}%`;
  } else if (v === 'bottom') {
    style.bottom = `${100 - y}%`;
  } else {
    style.top = `${y}%`;
  }

  const transforms: string[] = [];
  if (h === 'center') transforms.push('translateX(-50%)');
  if (v === 'center') transforms.push('translateY(-50%)');
  if (transforms.length) style.transform = transforms.join(' ');

  return style;
}

export function toneFromSlide(textColor: BlockSlide['textColor']) {
  const light = textColor !== 'dark';
  return {
    eyebrow: light ? 'text-white/60' : 'text-[#2E3245]/55',
    headline: light ? 'text-white' : 'text-[#2E3245]',
    sub: light ? 'text-white/80' : 'text-[#2E3245]/80',
    lede: light ? 'text-white/80' : 'text-[#2E3245]/75',
    body: light ? 'text-white/85' : 'text-[#2E3245]/85',
    caption: light ? 'text-white/55' : 'text-[#2E3245]/55',
    accent: light ? 'text-white' : 'text-[#2E3245]',
  };
}

function textClassForKind(kind: TextBlock['kind']): string {
  switch (kind) {
    case 'eyebrow':
      return EYEBROW;
    case 'headline':
      return H1;
    case 'subheadline':
      return `${QUOTE_SM}`;
    case 'lede':
      return LEDE_DECK;
    case 'body':
      return BODY_DECK;
    case 'caption':
      return CAPTION;
    case 'accent':
      return `${ACCENT} text-sm md:text-base`;
    default:
      return BODY_DECK;
  }
}

function textColorClass(kind: TextBlock['kind'], t: ReturnType<typeof toneFromSlide>): string {
  switch (kind) {
    case 'eyebrow':
      return t.eyebrow;
    case 'headline':
      return t.headline;
    case 'subheadline':
      return t.sub;
    case 'lede':
      return t.lede;
    case 'body':
      return t.body;
    case 'caption':
      return t.caption;
    case 'accent':
      return t.accent;
    default:
      return t.body;
  }
}

export interface BlockSlideViewProps {
  slide: BlockSlide;
  onTextChange?: (blockId: string, text: string) => void;
  onQrChange?: (blockId: string, patch: { value?: string; caption?: string }) => void;
  /** When set (editor canvas), each bullet row is contentEditable and patches `items` on blur. */
  onBulletsItemChange?: (blockId: string, itemIndex: number, text: string) => void;
  className?: string;
}

/**
 * Per-block size only — no positioning. Width comes from `block.w` (% of slide,
 * converted to fixed pixels so the value is stable inside any cell). The block
 * is positioned by its parent anchor cell.
 */
export function blockSizeStyle(block: Block): React.CSSProperties {
  if (!('w' in block) || block.w == null) return {};
  const widthPx = Math.round(block.w * SLIDE_PX_PER_PCT);
  return { width: `${widthPx}px`, maxWidth: '100%' };
}

/**
 * Combined size + post-spacing for a block in its anchor cell.
 *   `isLast` drops the bottom margin so the cell hugs its content cleanly.
 */
export function blockLayoutStyle(block: Block, isLast: boolean): React.CSSProperties {
  const size = blockSizeStyle(block);
  if (isLast) return size;
  return { ...size, marginBottom: `${spacingAfterKind(block.kind)}px` };
}

export function DeckSlideChrome({
  slide,
  className,
  children,
}: {
  slide: BlockSlide;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <SlideFrame id={slide.id} bg={slide.bg} bgColor={slide.bg?.color} className={className}>
      {slide.scrim && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{ background: scrimToCss(slide.scrim) }}
        />
      )}
      <div className="pointer-events-none relative z-10 h-full min-h-screen w-full">
        {children}
      </div>
    </SlideFrame>
  );
}

/** Group blocks by anchor preserving in-array order — stable for renderers and editors. */
export function groupBlocksByAnchor(blocks: Block[]): Record<Anchor, Block[]> {
  const out = {} as Record<Anchor, Block[]>;
  for (const a of ANCHORS) out[a] = [];
  for (const b of blocks) (out[b.anchor] ??= []).push(b);
  return out;
}

export function BlockSlideView({
  slide,
  onTextChange,
  onQrChange,
  onBulletsItemChange,
  className,
}: BlockSlideViewProps) {
  const t = toneFromSlide(slide.textColor);
  const groups = groupBlocksByAnchor(slide.blocks);

  return (
    <DeckSlideChrome slide={slide} className={className}>
      {ANCHORS.map((anchor) => {
        const blocks = groups[anchor];
        if (!blocks?.length) return null;
        return (
          <div key={anchor} style={anchorCellStyle(anchor)}>
            {blocks.map((block, i) => (
              <div key={block.id} style={blockLayoutStyle(block, i === blocks.length - 1)}>
                <BlockContent
                  block={block}
                  tone={t}
                  onTextChange={onTextChange}
                  onQrChange={onQrChange}
                  onBulletsItemChange={onBulletsItemChange}
                />
              </div>
            ))}
          </div>
        );
      })}
    </DeckSlideChrome>
  );
}

export function BlockContent({
  block,
  tone: tr,
  onTextChange,
  onQrChange,
  onBulletsItemChange,
}: {
  block: Block;
  tone: ReturnType<typeof toneFromSlide>;
  onTextChange?: (id: string, v: string) => void;
  onQrChange?: (id: string, p: { value?: string; caption?: string }) => void;
  onBulletsItemChange?: (blockId: string, itemIndex: number, text: string) => void;
}) {
  if (block.kind === 'logo') {
    const w = block.w ?? 20;
    return (
      <div style={{ width: `${w}%`, opacity: block.opacity ?? 1 }}>
        <img src={block.src} alt="" className="h-auto w-full" />
      </div>
    );
  }

  if (block.kind === 'qr') {
    const w = block.w ?? 18;
    const px = Math.round((1920 * w) / 100);
    const size = Math.min(280, Math.max(120, px));
    return (
      <div className="flex flex-col items-center" style={{ width: `${w}%` }}>
        <div className="rounded-3xl border border-[#2E3245]/10 bg-white p-4 shadow-sm">
          <div style={{ width: size, height: size }} className="mx-auto">
            <QRCode
              value={block.value || 'https://robin.guide'}
              size={size}
              level="M"
              fgColor="#2E3245"
              bgColor="#FFFFFF"
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
        {block.caption != null &&
          (onQrChange ? (
            <EditableText
              as="p"
              className={`${ACCENT} mt-3 text-center text-sm text-[#2E3245] md:text-base`}
              value={block.caption ?? ''}
              onChange={(v) => onQrChange(block.id, { caption: v })}
            />
          ) : (
            <p className={`${ACCENT} mt-3 text-center text-sm text-[#2E3245] md:text-base`}>
              {block.caption}
            </p>
          ))}
      </div>
    );
  }

  if (block.kind === 'bullets') {
    const fs = block.fontSize ? { fontSize: `${block.fontSize}px` } : undefined;
    const editItems = Boolean(onBulletsItemChange);
    return (
      <ul
        className={`${DECK_BULLET_LIST} ${BODY_DECK} ${tr.body}`}
        style={fs}
      >
        {block.items.map((item, i) => (
          <li key={i} className={DECK_BULLET_ROW}>
            <span className={DECK_BULLET_MARKER} aria-hidden>
              {DECK_BULLET_GLYPH}
            </span>
            {editItems ? (
              <EditableText
                as="div"
                multiline
                className={`${DECK_BULLET_TEXT} inline-block w-full`}
                value={item}
                onChange={(v) => onBulletsItemChange?.(block.id, i, v)}
              />
            ) : (
              <span className={DECK_BULLET_TEXT}>{item}</span>
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (block.kind === 'conversation') {
    const onDark = true;
    /** Base px drives label, bubbles, and footer together (em-based layout). */
    const basePx = block.fontSize ?? 16;
    return (
      <div className="flex flex-col" style={{ fontSize: `${basePx}px`, gap: '0.5em' }}>
        {block.label && (
          <p
            className={`font-dank-mono-italic tracking-widest leading-snug ${tr.eyebrow}`}
            style={{ fontSize: '1.125em' }}
          >
            {block.label}
          </p>
        )}
        <div className="flex flex-col" style={{ gap: '0.5em' }}>
          {block.thread.map((line, i) => {
            const isUser = line.from === 'user';
            return (
              <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[94%] whitespace-pre-wrap font-inter leading-snug ${
                    isUser
                      ? onDark
                        ? 'bg-marine-green text-white'
                        : 'bg-[#34C759] text-white'
                      : onDark
                        ? 'border border-white/60 bg-white text-[#0f1114]'
                        : 'bg-[#E9E9EB] text-[#1C1C1E]'
                  }`}
                  style={{
                    fontSize: '1em',
                    padding: '0.5em 1em',
                    borderRadius: '1em',
                  }}
                >
                  {line.text}
                </div>
              </div>
            );
          })}
        </div>
        {block.footer && (
          <p
            className={`font-inter leading-snug ${tr.caption}`}
            style={{ fontSize: '0.75em', marginTop: '0.5em' }}
          >
            {block.footer}
          </p>
        )}
      </div>
    );
  }

  const a = block.textAlign ?? 'left';
  const align = a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
  const cls = `${textClassForKind(block.kind)} ${textColorClass(block.kind, tr)} ${align}`;
  const fs = block.fontSize ? { fontSize: `${block.fontSize}px` } : {};
  const color = block.color ? { color: block.color } : {};

  const asTag =
    block.kind === 'headline' || block.kind === 'subheadline' ? ('h2' as const) : ('p' as const);

  return (
    <EditableText
      as={asTag}
      multiline={block.kind !== 'eyebrow' && block.kind !== 'accent'}
      className={cls}
      style={{ ...fs, ...color }}
      value={block.text}
      onChange={onTextChange ? (v) => onTextChange(block.id, v) : undefined}
    />
  );
}
